import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getImageAnalysis, updateImagesMetadata } from '../../services/Mercadeo/imageService';
import { ImageAnalysis, ProcessedImage, BatchUpdateResponse } from '../../types/imageMetadata';
import { putImageProductMetaData } from '../../services/Mercadeo/productoService';
import Swal from 'sweetalert2';

const CLOUDFRONT_BASE_URL = 'https://dddaq7mtrlhh7.cloudfront.net';

const S3MetadataManager: React.FC = () => {
    // Estados
    const [analysis, setAnalysis] = useState<ImageAnalysis | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [updating, setUpdating] = useState<boolean>(false);
    const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
    const [updateResults, setUpdateResults] = useState<BatchUpdateResponse | null>(null);
    const [allImages, setAllImages] = useState<ProcessedImage[]>([]);
    const [hoveredImage, setHoveredImage] = useState<string | null>(null);
    const [filters, setFilters] = useState({
        showOnlyNeedingUpdate: false,
        showInvalidSkus: false
    });
    const [pageSize, setPageSize] = useState<number>(100);
    const [appendNewImages, setAppendNewImages] = useState<boolean>(true);
    const [currentContinuationToken, setCurrentContinuationToken] = useState<string | undefined>(undefined);
    const [totalImages, setTotalImages] = useState<number>(0);
    const [showUpdateResults, setShowUpdateResults] = useState<boolean>(false); // ✅ Nuevo estado para controlar visibilidad
    const [lotesCargados, setLotesCargados] = useState<number>(1);
    const [autoProcessing, setAutoProcessing] = useState<boolean>(false);
    const [currentAutoBatch, setCurrentAutoBatch] = useState<number>(1);
    const [totalAutoBatches, setTotalAutoBatches] = useState<number>(0);

    const { user } = useAuth();

    // ✅ FUNCIONES CENTRALIZADAS PARA MANEJO CONSISTENTE DE METADATA
    const getMetadataValue = (metadata: any, keys: string[]): string | undefined => {
        if (!metadata) return undefined;

        for (const key of keys) {
            if (metadata[key] !== undefined && metadata[key] !== null && metadata[key] !== '') {
                return metadata[key].toString();
            }
        }
        return undefined;
    };

    const getUsoImagen = (image: ProcessedImage): string => {
        // Maneja ambos formatos: snake_case y camelCase
        const usoImagen = getMetadataValue(image.metadata, [
            'uso_imagen',    // Nuevo formato snake_case
            'usoimagen'      // Formato camelCase que viene del Lambda
        ]);

        return usoImagen || 'GENERAL';
    };

    const getNeedsOracleUpdate = (image: ProcessedImage): string | undefined => {
        return getMetadataValue(image.metadata, [
            'needs_oracle_update',  // Nuevo formato
            'needsoracleupdate'     // Formato camelCase
        ]);
    };

    const getOracleSyncStatus = (image: ProcessedImage): string | undefined => {
        return getMetadataValue(image.metadata, [
            'oracle_sync_status',
            'oraclesyncstatus'
        ]);
    };

    const getOracleLastSyncAt = (image: ProcessedImage): string | undefined => {
        return getMetadataValue(image.metadata, [
            'oracle_last_sync_at',
            'oraclelastsyncat'
        ]);
    };

    const isValidSkuFormat = (sku: string | null): boolean => {
        return sku ? /^\d{6}$/.test(sku) : false;
    };

    const needsUpdate = (image: ProcessedImage): boolean => {
        const usoImagen = getUsoImagen(image);
        const skuValid = isValidSkuFormat(image.skuFromFileName);
        const metadataComplete = image.metadataStatus === 'complete';
        const needsOracleUpdate = getNeedsOracleUpdate(image);
        const oracleLastSyncAt = getOracleLastSyncAt(image);

        // ✅ TODAS las imágenes necesitan update si no tienen metadata completa
        if (!metadataComplete) {
            return true;
        }

        // ✅ Las imágenes PRINCIPAL necesitan update si no están sincronizadas con Oracle
        if (usoImagen === 'PRINCIPAL' && skuValid) {
            return needsOracleUpdate === 'true' ||
                !oracleLastSyncAt ||
                oracleLastSyncAt === 'N/A' ||
                oracleLastSyncAt === 'pending';
        }

        return false;
    };

    // ✅ LISTA MAESTRA FILTRADA (ÚNICA FUENTE DE VERDAD)
    const getDisplayImages = (): ProcessedImage[] => {
        let filtered = allImages;

        // Filtrar por SKUs válidos/inválidos
        if (!filters.showInvalidSkus) {
            filtered = filtered.filter(img => isValidSkuFormat(img.skuFromFileName));
        }

        // Filtrar por necesidades de actualización
        if (filters.showOnlyNeedingUpdate) {
            filtered = filtered.filter(img => needsUpdate(img));
        }

        return filtered;
    };

    const shouldShowOracleButton = (image: ProcessedImage): boolean => {
        const usoImagen = getUsoImagen(image);
        const skuValid = isValidSkuFormat(image.skuFromFileName);
        const needsOracleUpdate = getNeedsOracleUpdate(image);

        // ✅ Solo mostrar botón Oracle para imágenes PRINCIPAL con SKU válido que necesiten update
        return usoImagen === 'PRINCIPAL' &&
            skuValid &&
            (needsOracleUpdate === 'true' ||
                needsOracleUpdate === null ||
                needsOracleUpdate === undefined);
    };

    // ✅ FORMATO DE METADATA PARA TOOLTIP
    const formatMetadataForTooltip = (metadata: any): string => {
        if (!metadata || Object.keys(metadata).length === 0) {
            return 'No hay metadata disponible';
        }

        return Object.entries(metadata)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
    };

    // ✅ SWEETALERT2 HELPERS
    const showSuccessAlert = async (title: string, message: string) => {
        Swal.fire({
            icon: 'success',
            title,
            text: message,
            timer: 3000,
            showConfirmButton: false
        });
    };

    const showErrorAlert = async (title: string, message: string) => {
        Swal.fire({
            icon: 'error',
            title,
            text: message,
            confirmButtonText: 'Entendido'
        });
    };

    const showWarningAlert = async (title: string, message: string) => {
        Swal.fire({
            icon: 'warning',
            title,
            text: message,
            confirmButtonText: 'Entendido'
        });
    };

    const showConfirmDialog = async (title: string, text: string): Promise<boolean> => {
        const result = await Swal.fire({
            icon: 'question',
            title,
            text,
            showCancelButton: true,
            confirmButtonText: 'Sí, continuar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
        });
        return result.isConfirmed;
    };

    const showLoadingAlert = async (title: string, text: string) => {
        Swal.fire({
            title,
            text,
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
    };

    const closeLoadingAlert = () => {
        Swal.close();
    };

    // ✅ CARGAR ANÁLISIS CON PAGE SIZE
    const loadAnalysis = async (continuationToken?: string, loadMore: boolean = false): Promise<void> => {
        if (loadMore) {
            setLoadingMore(true);
            setLotesCargados(prev => prev + 1);
        } else {
            setLoading(true);
            if (continuationToken === undefined || continuationToken === null) {
                setLotesCargados(1);
            }
        }

        try {
            const result = await getImageAnalysis(continuationToken, pageSize);
            setCurrentContinuationToken(continuationToken);

            if (result.pagination.exactTotalCalculated && result.pagination.exactTotal) {
                setTotalImages(result.pagination.exactTotal);
            }


            // ✅ DEBUG: Verificar metadata de algunas imágenes
            if (result.currentBatch.images.length > 0) {
                console.log('🔍 Sample metadata formats from analysis:');
                result.currentBatch.images.slice(0, 3).forEach((img, index) => {
                    console.log(`   ${index + 1}. ${img.fileName}:`, {
                        metadataKeys: Object.keys(img.metadata || {}),
                        usoImagen: getUsoImagen(img),
                        needsOracleUpdate: getNeedsOracleUpdate(img),
                        rawMetadata: img.metadata
                    });
                });
            }
            // // ✅ LÓGICA CORRECTA PARA CONTADOR DE LOTES
            // if (continuationToken === undefined || continuationToken === null) {
            //     // ✅ CARGA INICIAL: reiniciar contador a 1
            //     setLotesCargados(1);
            // } else {
            //     setLotesCargados(prev => prev + 1);
            // }

            if (loadMore && analysis && appendNewImages) {
                //setLotesCargados(prev => prev + 1);
                setAnalysis(result);
                setAllImages(prevImages => [...prevImages, ...result.currentBatch.images]);

            } else {
                setAnalysis(result);
                setAllImages(result.currentBatch.images);
            }

            // ✅ Ocultar resultados de actualización en cada nueva carga

            if (!updating) {
                setShowUpdateResults(false);
                setUpdateResults(null);
            }

        } catch (error) {
            console.error('Error loading analysis:', error);
            showErrorAlert('Error al cargar análisis', 'Error al cargar el análisis: ' + (error as Error).message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadMoreImages = async (): Promise<void> => {
        if (analysis?.pagination.hasMore && analysis.pagination.nextContinuationToken) {
            await loadAnalysis(analysis.pagination.nextContinuationToken, true);
        }
    };

    // ✅ SELECCIÓN MEJORADA
    const handleSelectImage = (imageKey: string, selected: boolean): void => {
        setSelectedImages(prev => {
            const newSelected = new Set(prev);
            selected ? newSelected.add(imageKey) : newSelected.delete(imageKey);
            return newSelected;
        });
    };

    const selectAllNeedingUpdate = (): number => {
        const displayImages = getDisplayImages();
        const needsUpdateImages = displayImages.filter(img => needsUpdate(img));

        // ✅ SEPARAR POR PRIORIDAD: PRINCIPAL primero
        const principalImages = needsUpdateImages.filter(img =>
            getUsoImagen(img) === 'PRINCIPAL'
        );

        const generalImages = needsUpdateImages.filter(img =>
            getUsoImagen(img) !== 'PRINCIPAL'
        );

        // ✅ COMBINAR: PRINCIPAL primero, luego GENERAL (hasta 50)
        let finalSelection: ProcessedImage[] = [];

        // Agregar PRINCIPAL (máximo 50)
        if (principalImages.length > 0) {
            finalSelection = principalImages.slice(0, 50);
        }

        // Si hay espacio, agregar GENERAL
        if (finalSelection.length < 50) {
            const remainingSlots = 50 - finalSelection.length;
            finalSelection = [...finalSelection, ...generalImages.slice(0, remainingSlots)];
        }

        console.log('🎯 Selección finalizada:', {
            totalNeedingUpdate: needsUpdateImages.length,
            principal: principalImages.length,
            general: generalImages.length,
            selected: finalSelection.length,
            principalSelected: finalSelection.filter(img => getUsoImagen(img) === 'PRINCIPAL').length
        });

        if (needsUpdateImages.length > 50) {
            showWarningAlert(
                'Selección limitada',
                `Se seleccionaron ${finalSelection.length} imágenes (${finalSelection.filter(img => getUsoImagen(img) === 'PRINCIPAL').length} PRINCIPAL) de ${needsUpdateImages.length} disponibles.`
            );
        }

        setSelectedImages(new Set(finalSelection.map(img => img.key)));

        return finalSelection.length;
    };

    // ✅ ACTUALIZACIÓN MEJORADA (AWS + Oracle)
    const updateSelectedImages = async (): Promise<void> => {
        const displayImages = getDisplayImages();
        const selectedImagesToUpdate = displayImages.filter(img => selectedImages.has(img.key));

        // ✅ DEBUG DETALLADO
        console.log('🔍 Imágenes seleccionadas para actualizar:', {
            total: selectedImagesToUpdate.length,
            byUsoImagen: {
                PRINCIPAL: selectedImagesToUpdate.filter(img => getUsoImagen(img) === 'PRINCIPAL').length,
                GENERAL: selectedImagesToUpdate.filter(img => getUsoImagen(img) === 'GENERAL').length
            },
            bySkuValidity: {
                valid: selectedImagesToUpdate.filter(img => isValidSkuFormat(img.skuFromFileName)).length,
                invalid: selectedImagesToUpdate.filter(img => !isValidSkuFormat(img.skuFromFileName)).length
            }
        });

        if (selectedImagesToUpdate.length === 0) {
            showWarningAlert('Sin selección', 'No hay imágenes seleccionadas');
            return;
        }

        if (selectedImagesToUpdate.length > pageSize) {
            showErrorAlert(
                'Límite excedido',
                `Máximo ${pageSize} imágenes por lote. Selecciona menos imágenes.`
            );
            return;
        }

        // ✅ ADVERTENCIA PARA SKUs INVÁLIDOS
        const invalidSkus = selectedImagesToUpdate.filter(img => !isValidSkuFormat(img.skuFromFileName));
        if (invalidSkus.length > 0) {
            const proceed = await showConfirmDialog(
                'SKUs no válidos detectados',
                `${invalidSkus.length} imágenes tienen SKUs no válidos (no son 6 dígitos).\n\nEstas imágenes se actualizarán en S3 pero NO en Oracle.\n\n¿Continuar con la actualización?`
            );
            if (!proceed) return;
        }

        const confirmUpdate = await showConfirmDialog(
            'Confirmar actualización',
            `¿Estás seguro de que deseas actualizar ${selectedImagesToUpdate.length} imágenes?\n\n• ${selectedImagesToUpdate.filter(img => getUsoImagen(img) === 'PRINCIPAL').length} imágenes PRINCIPAL (S3 + Oracle)\n• ${selectedImagesToUpdate.filter(img => getUsoImagen(img) === 'GENERAL').length} imágenes GENERAL (S3 solamente)`
        );

        if (!confirmUpdate) return;

        setUpdating(true);
        showLoadingAlert('Actualizando imágenes', 'Procesando actualización de metadata...');

        try {
            const defaultMetadata = {
                usuario: user?.User.email || 'sistema@grupociisa.com',
                ultimo_editor: user?.User.email || 'sistema@grupociisa.com',
                uso_imagen: 'GENERAL' // Valor por defecto
            };

            const oracleResults: Record<string, any> = {};

            // ✅ SOLO imágenes PRINCIPAL con SKUs válidos para Oracle
            const principalImages = selectedImagesToUpdate.filter(img =>
                getUsoImagen(img) === 'PRINCIPAL' &&
                isValidSkuFormat(img.skuFromFileName)
            );

            console.log('🎯 Imágenes PRINCIPAL para Oracle:', principalImages.length);
            principalImages.forEach(img => {
                console.log('   - Oracle target:', img.fileName, 'SKU:', img.skuFromFileName, 'Uso:', getUsoImagen(img));
            });

            // ✅ ACTUALIZAR ORACLE SOLO PARA PRINCIPAL
            const oraclePromises = principalImages.map(async (image) => {
                try {
                    console.log('Actualizando en Oracle la imagen:', image.fileName, 'para SKU:', image.skuFromFileName);

                    const oracleResponse = await putImageProductMetaData(image.skuFromFileName!, {
                        imageName: image.fileName,
                        usoImagen: 'PRINCIPAL',
                        usuario: user?.User.email || ''
                    });

                    oracleResults[image.key] = {
                        success: oracleResponse.status === 'success',
                        error: oracleResponse.status === 'success' ? null : oracleResponse.message
                    };
                    console.log('Oracle update response for', image.fileName, ':', oracleResponse.status);
                } catch (error) {
                    console.error('Error updating Oracle for', image.fileName, ':', error);
                    oracleResults[image.key] = {
                        success: false,
                        error: (error as Error).message
                    };
                }
            });

            await Promise.allSettled(oraclePromises);

            console.log('📊 Resultados de actualizaciones en Oracle:', oracleResults);
            console.log('Total de resultados de Oracle:', Object.keys(oracleResults).length);

            // ✅ ACTUALIZAR TODAS LAS IMÁGENES EN S3 (PRINCIPAL + GENERAL)
            const result = await updateImagesMetadata(selectedImagesToUpdate, defaultMetadata, oracleResults);
            setUpdateResults(result);
            setShowUpdateResults(true); // ✅ Mostrar resultados después de actualizar

            closeLoadingAlert();

            if (result.results?.failed > 0) {
                showWarningAlert(
                    'Actualización completada con advertencias',
                    `Se completó la actualización: ${result.results.successful} exitosas, ${result.results.failed} fallidas`
                );
            } else {
                showSuccessAlert(
                    'Actualización exitosa',
                    `Todas las ${result.results?.successful} imágenes se actualizaron correctamente`
                );
            }

            if(!autoProcessing){
                await loadAnalysis(currentContinuationToken, false);
            }
            setSelectedImages(new Set());

        } catch (error) {
            console.error('Error updating images:', error);
            closeLoadingAlert();
            showErrorAlert('Error en actualización', 'Error al actualizar imágenes: ' + (error as Error).message);
        } finally {
            setUpdating(false);
        }
    };

    // ✅ ACTUALIZACIÓN INDIVIDUAL ORACLE
    const updateSingleImageInOracle = async (image: ProcessedImage): Promise<void> => {
        const confirmUpdate = await showConfirmDialog(
            'Actualizar en Oracle',
            `¿Estás seguro de que deseas actualizar la imagen ${image.fileName} en Oracle?`
        );

        if (!confirmUpdate) return;

        showLoadingAlert('Actualizando Oracle', 'Procesando actualización en Oracle...');

        try {
            await putImageProductMetaData(image.skuFromFileName!, {
                imageName: image.fileName,
                usoImagen: 'PRINCIPAL',
                usuario: user?.User.email || ''
            });

            closeLoadingAlert();
            showSuccessAlert('Oracle actualizado', '✅ Imagen actualizada en Oracle correctamente');

            await loadAnalysis(currentContinuationToken, false);
        } catch (error) {
            console.error('Error updating Oracle:', error);
            closeLoadingAlert();
            showErrorAlert('Error en Oracle', '❌ Error al actualizar en Oracle: ' + (error as Error).message);
        }
    };

    // ✅ CERRAR RESULTADOS DE ACTUALIZACIÓN
    const handleCloseUpdateResults = (): void => {
        setShowUpdateResults(false);
        //setUpdateResults(null);
    };

    // ✅ ESTADÍSTICAS MEJORADAS
    const getStats = () => {
        const displayImages = getDisplayImages();
        const validImages = allImages.filter(img => isValidSkuFormat(img.skuFromFileName));

        return {
            total: allImages.length,
            totalValid: validImages.length,
            totalInvalid: allImages.length - validImages.length,
            displayCount: displayImages.length,
            complete: validImages.filter(img => img.metadataStatus === 'complete').length,
            partial: validImages.filter(img => img.metadataStatus === 'partial').length,
            none: validImages.filter(img => img.metadataStatus === 'none').length,
            needsUpdate: displayImages.filter(img => needsUpdate(img)).length,
            principal: validImages.filter(img => getUsoImagen(img) === 'PRINCIPAL').length,
            principalNeedingUpdate: validImages.filter(img =>
                getUsoImagen(img) === 'PRINCIPAL' && needsUpdate(img)
            ).length,
            generalNeedingUpdate: validImages.filter(img =>
                getUsoImagen(img) === 'GENERAL' && needsUpdate(img)
            ).length,
            selectedCount: selectedImages.size,
            selectedPrincipal: Array.from(selectedImages).filter(key => {
                const img = allImages.find(i => i.key === key);
                return img && getUsoImagen(img) === 'PRINCIPAL';
            }).length,
            selectedGeneral: Array.from(selectedImages).filter(key => {
                const img = allImages.find(i => i.key === key);
                return img && getUsoImagen(img) === 'GENERAL';
            }).length
        };
    };

    // ✅ PROCESO AUTOMÁTICO
    const startAutoProcess = async (): Promise<void> => {
        if (!analysis) {
            showErrorAlert('Error', 'Primero debe cargar el análisis');
            return;
        }

        // ✅ USAR el total del análisis en lugar de totalImages (que puede ser 0)
        const totalImagesCount = totalImages > 0 ? totalImages : analysis.pagination.estimatedTotal;

        if (!totalImagesCount || totalImagesCount === 0) {
            showErrorAlert('Error', 'No se pudo determinar el total de imágenes');
            return;
        }

        const totalBatches = Math.ceil(totalImagesCount / pageSize);

        console.log('🚀 Iniciando proceso automático:', {
            totalImages: totalImagesCount,
            pageSize,
            totalBatches
        });

        // ✅ PRIMERO establecer todos los estados
        setTotalAutoBatches(totalBatches);
        setCurrentAutoBatch(1);
        setAutoProcessing(true);

        // ✅ Pequeña pausa para asegurar que React actualice el estado
        setTimeout(() => {
            processAutoBatch();
        }, 100);
    };

    const processAutoBatch = async (): Promise<void> => {
        // ✅ Verificar el estado actual en el momento de la ejecución
        const shouldProcess = autoProcessing && analysis;

        console.log('🔄 processAutoBatch llamado:', {
            autoProcessing,
            shouldProcess,
            currentAutoBatch,
            totalAutoBatches
        });

        if (!shouldProcess) {
            console.log('❌ Auto process stopped:', {
                autoProcessing,
                hasAnalysis: !!analysis,
                currentAutoBatch,
                totalAutoBatches
            });
            return;
        }

        console.log('🔄 Procesando lote:', {
            currentBatch: currentAutoBatch,
            totalBatches: totalAutoBatches
        });

        const confirmContinue = await showConfirmDialog(
            'Proceso Automático',
            `¿Procesar lote ${currentAutoBatch} de ${totalAutoBatches}?\n\nSe seleccionarán y actualizarán automáticamente las imágenes que necesiten actualización.`
        );

        if (!confirmContinue) {
            console.log('❌ Usuario canceló el proceso');
            setAutoProcessing(false);
            return;
        }

        try {
            // ✅ 1. Seleccionar imágenes que necesitan update en el lote actual
            const selectedCount = selectAllNeedingUpdate();
            console.log('✅ Imágenes seleccionadas:', selectedCount);

            // ✅ Pequeña pausa para que React actualice el estado
            await new Promise(resolve => setTimeout(resolve, 500));

            // ✅ 2. Verificar si realmente hay imágenes seleccionadas
            if (selectedImages.size === 0) {
                console.log('⚠️ No hay imágenes seleccionadas, saltando al siguiente lote');
                showWarningAlert(
                    'Sin imágenes para actualizar',
                    `No hay imágenes que necesiten actualización en el lote ${currentAutoBatch}. Pasando al siguiente lote...`
                );

                // ✅ Saltar al siguiente lote si no hay imágenes
                await proceedToNextBatch();
                return;
            }

            console.log('🔄 Iniciando actualización de imágenes seleccionadas...');

            // ✅ 3. Actualizar las imágenes seleccionadas
            await updateSelectedImages();

            console.log('✅ Actualización completada, procediendo al siguiente lote...');

            // ✅ 4. Proceder al siguiente lote
            await proceedToNextBatch();

        } catch (error) {
            console.error('Error en proceso automático:', error);
            setAutoProcessing(false);
            showErrorAlert(
                'Error en Proceso Automático',
                `Error procesando lote ${currentAutoBatch}: ${(error as Error).message}`
            );
        }
    };


    // ✅ FUNCIÓN PARA PROCEDER AL SIGUIENTE LOTE
    const proceedToNextBatch = async (): Promise<void> => {
        // ✅ Verificar el estado actual
        if (!autoProcessing) {
            console.log('❌ proceedToNextBatch: autoProcessing es false, deteniendo');
            return;
        }

        // ✅ Verificar si hay más lotes
        const hasMoreBatches = currentAutoBatch < totalAutoBatches;
        const hasMoreImages = analysis?.pagination.hasMore;

        console.log('➡️ Verificando siguiente lote:', {
            currentBatch: currentAutoBatch,
            totalBatches: totalAutoBatches,
            hasMoreBatches,
            hasMoreImages,
            autoProcessing
        });

        if (hasMoreBatches && hasMoreImages) {
            console.log('📈 Incrementando contador de lote...');
            setCurrentAutoBatch(prev => prev + 1);

            // ✅ Pausa antes de cargar el siguiente lote
            setTimeout(async () => {
                console.log('📥 Cargando siguiente lote...');
                try {
                    await loadMoreImages();

                    // ✅ Pausa antes de procesar el siguiente lote
                    setTimeout(() => {
                        console.log('🔄 Iniciando procesamiento del siguiente lote...');
                        // ✅ Verificar que aún estamos en modo automático
                        if (autoProcessing) {
                            processAutoBatch();
                        } else {
                            console.log('❌ Auto processing fue detenido durante la carga');
                        }
                    }, 2000);

                } catch (error) {
                    console.error('❌ Error cargando siguiente lote:', error);
                    setAutoProcessing(false);
                    showErrorAlert('Error', 'Error cargando el siguiente lote: ' + (error as Error).message);
                }
            }, 2000);
        } else {
            // ✅ Proceso completado
            console.log('🎉 Proceso automático completado');
            setAutoProcessing(false);
            showSuccessAlert(
                'Proceso Automático Completado',
                `Se procesaron todos los ${totalAutoBatches} lotes exitosamente.`
            );
        }
    };

    // ✅ DETENER PROCESO AUTOMÁTICO
    const stopAutoProcess = (): void => {
        setAutoProcessing(false);
        showWarningAlert(
            'Proceso Detenido',
            `El proceso automático se detuvo en el lote ${currentAutoBatch} de ${totalAutoBatches}`
        );
    };

    // ✅ EFFECTS
    useEffect(() => {
        loadAnalysis();
    }, []);

    useEffect(() => {
        if (analysis) {
            loadAnalysis();
        }
    }, [pageSize]);

    // ✅ DEBUG: Monitorear cambios en autoProcessing
    useEffect(() => {
        console.log('🔍 autoProcessing cambió:', autoProcessing);
    }, [autoProcessing]);

    useEffect(() => {
        console.log('🔍 currentAutoBatch cambió:', currentAutoBatch);
    }, [currentAutoBatch]);

    useEffect(() => {
        console.log('🔍 totalAutoBatches cambió:', totalAutoBatches);
    }, [totalAutoBatches]);

    const stats = getStats();
    const displayImages = getDisplayImages();
    const hasMore = analysis?.pagination.hasMore || false;

    return (
        <div className="container-fluid py-3">
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="h4 mb-0">🔄 Administrador de Metadata</h1>
                    <p className="text-muted small mb-0">Gestiona metadata de imágenes S3 y Oracle</p>
                </div>
                <button
                    onClick={() => loadAnalysis()}
                    disabled={loading}
                    className="btn btn-primary btn-sm"
                >
                    {loading ? '🔄 Cargando...' : '📊 Actualizar Análisis'}
                </button>
            </div>

            {/* ESTADÍSTICAS MEJORADAS */}
            {analysis && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body py-2">
                                <div className="row text-center">
                                    {[
                                        { value: stats.totalValid, label: 'Válidos', color: 'primary' },
                                        { value: stats.complete, label: 'Completas', color: 'success' },
                                        { value: stats.partial, label: 'Parciales', color: 'warning' },
                                        { value: stats.none, label: 'Sin Metadata', color: 'danger' },
                                        { value: stats.needsUpdate, label: 'Necesitan Update', color: 'danger' },
                                        { value: stats.principal, label: 'PRINCIPAL', color: 'info' },
                                        { value: stats.principalNeedingUpdate, label: 'PRINCIPAL con Update', color: 'warning' },
                                        { value: stats.generalNeedingUpdate, label: 'GENERAL con Update', color: 'secondary' }
                                    ].map((stat, index) => (
                                        <div key={index} className="col-md-2 col-4 mb-1">
                                            <div className={`h6 mb-0 text-${stat.color}`}>{stat.value}</div>
                                            <div className="text-muted small">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="text-center mt-2">
                                    <small className="text-muted">
                                        Mostrando {stats.displayCount} de {totalImages || analysis.pagination.estimatedTotal} imágenes •
                                        PRINCIPAL: {stats.principal} •
                                        {stats.selectedCount} seleccionadas ({stats.selectedPrincipal} PRINCIPAL, {stats.selectedGeneral} GENERAL)
                                        {stats.totalInvalid > 0 && ` • ${stats.totalInvalid} SKUs inválidos`}
                                        {lotesCargados > 1 && ` • Lote ${lotesCargados}`}
                                        {autoProcessing && ` • Auto: ${currentAutoBatch}/${totalAutoBatches}`}
                                    </small>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* RESULTADOS DE ACTUALIZACIÓN - MEJORADO CON BOTÓN DE CERRAR */}
            {showUpdateResults && updateResults && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className={`alert ${updateResults.results?.failed > 0 ? 'alert-warning' : 'alert-success'} py-2 position-relative`}>
                            <button
                                type="button"
                                className="btn-close position-absolute top-0 end-0 m-2"
                                onClick={handleCloseUpdateResults}
                                aria-label="Cerrar"
                            ></button>
                            <strong>{updateResults.message}</strong>
                            <div className="mt-1">
                                <span className="text-success">✓ {updateResults.results?.successful} exitosas</span>
                                {updateResults.results?.failed > 0 && (
                                    <span className="text-danger ms-3">✗ {updateResults.results?.failed} fallidas</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CONTROLES MEJORADOS */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body py-2">
                            <div className="row align-items-center">
                                <div className="col-md-4">
                                    <div className="d-flex gap-3">
                                        <div className="form-check form-switch">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={filters.showOnlyNeedingUpdate}
                                                onChange={(e) => setFilters(prev => ({
                                                    ...prev,
                                                    showOnlyNeedingUpdate: e.target.checked
                                                }))}
                                            />
                                            <label className="form-check-label small">
                                                Solo necesitan actualización
                                            </label>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={filters.showInvalidSkus}
                                                onChange={(e) => setFilters(prev => ({
                                                    ...prev,
                                                    showInvalidSkus: e.target.checked
                                                }))}
                                            />
                                            <label className="form-check-label small">
                                                Mostrar SKUs inválidos
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-check form-switch">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={appendNewImages}
                                            onChange={(e) => setAppendNewImages(e.target.checked)}
                                        />
                                        <label className="form-check-label small">
                                            Acumular imágenes
                                        </label>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text">Por página:</span>
                                        <select
                                            className="form-select"
                                            value={pageSize}
                                            onChange={(e) => setPageSize(Number(e.target.value))}
                                        >
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="d-flex gap-2">
                                        <button
                                            onClick={selectAllNeedingUpdate}
                                            className="btn btn-outline-secondary btn-sm"
                                        >
                                            Seleccionar ({Math.min(stats.needsUpdate, 50)})
                                        </button>
                                        <button
                                            onClick={() => setSelectedImages(new Set())}
                                            className="btn btn-outline-secondary btn-sm"
                                        >
                                            Limpiar
                                        </button>
                                        <button
                                            onClick={updateSelectedImages}
                                            disabled={updating || stats.selectedCount === 0}
                                            className="btn btn-warning btn-sm"
                                        >
                                            {updating ? '🔄 Actualizando...' : `📤 Actualizar ${stats.selectedCount}`}
                                        </button>
                                        {/* ✅ BOTÓN DE PROCESO AUTOMÁTICO */}
                                        {!autoProcessing ? (
                                            <button
                                                onClick={startAutoProcess}
                                                disabled={!analysis || loading}
                                                className="btn btn-success btn-sm"
                                                title="Procesar todos los lotes automáticamente"
                                            >
                                                🤖 Auto Proceso
                                            </button>
                                        ) : (
                                            <button
                                                onClick={stopAutoProcess}
                                                className="btn btn-danger btn-sm"
                                            >
                                                ⏹️ Detener ({currentAutoBatch}/{totalAutoBatches})
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* ✅ BARRA DE PROGRESO DEL PROCESO AUTOMÁTICO */}
                            {autoProcessing && (
                                <div className="row mt-2">
                                    <div className="col-12">
                                        <div className="progress" style={{ height: '20px' }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                role="progressbar"
                                                style={{ width: `${(currentAutoBatch / totalAutoBatches) * 100}%` }}
                                                aria-valuenow={currentAutoBatch}
                                                aria-valuemin={1}
                                                aria-valuemax={totalAutoBatches}
                                            >
                                                Procesando: {currentAutoBatch} de {totalAutoBatches} lotes
                                            </div>
                                        </div>
                                        <small className="text-muted d-block text-center mt-1">
                                            Procesando lote {currentAutoBatch} de {totalAutoBatches} •
                                            Total imágenes: {totalImages} •
                                            Tamaño de lote: {pageSize}
                                        </small>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* LISTA DE IMÁGENES */}
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center py-2">
                            <h6 className="card-title mb-0">
                                🖼️ Imágenes ({stats.displayCount})
                                {filters.showOnlyNeedingUpdate && ` • ${stats.needsUpdate} necesitan update`}
                                {filters.showInvalidSkus && ` • ${stats.totalInvalid} SKUs inválidos`}
                                {lotesCargados > 1 && ` • Lote ${lotesCargados}`}
                            </h6>
                            <div className="d-flex gap-2">
                                {hasMore && (
                                    <button
                                        onClick={loadMoreImages}
                                        disabled={loadingMore}
                                        className="btn btn-outline-primary btn-sm"
                                    >
                                        {loadingMore ? '📥 Cargando...' : `📥 Cargar Más (Lote ${lotesCargados + 1})`}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status"></div>
                                    <p className="mt-2 small">Cargando imágenes...</p>
                                </div>
                            ) : displayImages.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    No hay imágenes para mostrar
                                </div>
                            ) : (
                                <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                                    <table className="table table-hover table-striped mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>ID</th>
                                                <th style={{ width: '40px' }}>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={stats.selectedCount > 0 && stats.selectedCount === Math.min(stats.needsUpdate, 50)}
                                                        onChange={(e) => e.target.checked ? selectAllNeedingUpdate() : setSelectedImages(new Set())}
                                                    />
                                                </th>
                                                <th style={{ width: '80px' }}>Imagen</th>
                                                <th>Archivo</th>
                                                <th>SKU Metadata</th>
                                                <th>SKU Archivo</th>
                                                <th>Tamaño</th>
                                                <th>Estado Metadata</th>
                                                <th>Validez SKU</th>
                                                <th>Última Modificación</th>
                                                <th>Uso</th>
                                                <th style={{ width: '100px' }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displayImages.map((image, index) => (
                                                <tr key={`${image.key}-${index}`} className={needsUpdate(image) ? 'table-warning' : ''}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        {needsUpdate(image) && (
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedImages.has(image.key)}
                                                                onChange={(e) => handleSelectImage(image.key, e.target.checked)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div
                                                            className="position-relative"
                                                            onMouseEnter={() => setHoveredImage(image.key)}
                                                            onMouseLeave={() => setHoveredImage(null)}
                                                        >
                                                            <img
                                                                src={`${CLOUDFRONT_BASE_URL}/${image.key}`}
                                                                alt={image.fileName}
                                                                className="img-thumbnail"
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover', cursor: 'pointer' }}
                                                                onError={(e) => {
                                                                    const target = e.target as HTMLImageElement;
                                                                    target.style.display = 'none';
                                                                }}
                                                            />
                                                            {/* TOOLTIP DE IMAGEN GRANDE CON METADATA */}
                                                            {hoveredImage === image.key && (
                                                                <div
                                                                    className="position-absolute start-100 top-0 ms-2 bg-white border rounded shadow-lg p-3"
                                                                    style={{
                                                                        zIndex: 1060,
                                                                        width: '400px',
                                                                        maxHeight: '80vh',
                                                                        overflow: 'auto'
                                                                    }}
                                                                >
                                                                    <div className="text-center mb-2">
                                                                        <img
                                                                            src={`${CLOUDFRONT_BASE_URL}/${image.key}`}
                                                                            alt={image.fileName}
                                                                            className="img-fluid rounded"
                                                                            style={{ maxHeight: '300px', objectFit: 'contain' }}
                                                                            onError={(e) => {
                                                                                const target = e.target as HTMLImageElement;
                                                                                target.style.display = 'none';
                                                                                target.nextElementSibling?.classList.remove('d-none');
                                                                            }}
                                                                        />
                                                                        <div className="d-none text-muted p-3">
                                                                            Imagen no disponible
                                                                        </div>
                                                                    </div>

                                                                    <div className="border-top pt-2">
                                                                        <pre className="small text-muted mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                                                                            {formatMetadataForTooltip(image.metadata)}
                                                                        </pre>
                                                                    </div>

                                                                    <div className="border-top pt-2 mt-2">
                                                                        <small className="text-muted">
                                                                            <strong>Archivo:</strong> {image.fileName}<br />
                                                                            <strong>SKU Archivo:</strong> {image.skuFromFileName || 'N/A'}<br />
                                                                            <strong>SKU Metadata:</strong> {image.skuFromMetadata || 'N/A'}<br />
                                                                            <strong>Estado:</strong> {image.metadataStatus}<br />
                                                                            <strong>Uso detectado:</strong> {getUsoImagen(image)}<br />
                                                                            <strong>Necesita Update:</strong> {needsUpdate(image) ? 'Sí' : 'No'}<br />
                                                                            <strong>Botón Oracle:</strong> {shouldShowOracleButton(image) ? 'SÍ' : 'NO'}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <code
                                                            className="small"
                                                            style={{ cursor: 'pointer' }}
                                                            title={formatMetadataForTooltip(image.metadata)}
                                                        >
                                                            {image.fileName}
                                                        </code>
                                                        {image.error && (
                                                            <div className="text-danger small">Error: {image.error}</div>
                                                        )}
                                                        {!isValidSkuFormat(image.skuFromFileName) && (
                                                            <div className="text-warning small">SKU no válido</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <code className="small">{image.skuFromMetadata || 'N/A'}</code>
                                                    </td>
                                                    <td>
                                                        <code className="small">{image.skuFromFileName || 'N/A'}</code>
                                                    </td>
                                                    <td className="small">
                                                        {(image.size / 1024).toFixed(2)} KB
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`badge ${image.metadataStatus === 'complete' ? 'bg-success' :
                                                                image.metadataStatus === 'partial' ? 'bg-warning' :
                                                                    image.metadataStatus === 'error' ? 'bg-danger' : 'bg-secondary'
                                                                }`}
                                                            title={formatMetadataForTooltip(image.metadata)}
                                                        >
                                                            {image.metadataStatus}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${isValidSkuFormat(image.skuFromFileName) ? 'bg-success' : 'bg-danger'
                                                            }`}>
                                                            {isValidSkuFormat(image.skuFromFileName) ? 'válido' : 'inválido'}
                                                        </span>
                                                    </td>
                                                    <td className="small">
                                                        {new Date(image.lastModified).toLocaleDateString('es-ES', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${getUsoImagen(image) === 'PRINCIPAL' ? 'bg-success' : 'bg-warning'
                                                            }`}>
                                                            {getUsoImagen(image)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {shouldShowOracleButton(image) ? (
                                                            <button
                                                                className="btn btn-outline-success btn-sm"
                                                                onClick={() => updateSingleImageInOracle(image)}
                                                                title="Actualizar en Oracle"
                                                            >
                                                                📊 Oracle
                                                            </button>
                                                        ) : (
                                                            <span className="badge bg-success" title="Oracle actualizado">
                                                                ✅ Oracle
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default S3MetadataManager;